import React, { useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatMonto, formatFecha } from '../utils/format';
import { aVista } from '../components/AntiguedadSaldos';
import SelectorMoneda, { useVistaMoneda } from '../components/reportes/SelectorMoneda';
import Exportar from '../components/reportes/Exportar';
import EncabezadoImpresion from '../components/reportes/EncabezadoImpresion';
import BotonAyuda from '../components/BotonAyuda';
import { saldoEnVista } from '../utils/indexacion';

// Cuentas por cobrar: las facturas de venta abiertas, una por una y por
// cliente, igual que la columna Pendientes de Cuentas por pagar. La
// antigüedad agrupada por tramos es el reporte aparte
// (/reportes/antiguedad-por-cobrar).

const pref = (ver) => (ver === 'BCV' ? 'BCV$ ' : '$');

// Una factura indexada (#151) cuenta lo que se debe hoy.
const montoVista = (f, verEn) => saldoEnVista(f, verEn, aVista);

// Días respecto al vencimiento (fecha + días de crédito), como en CxP.
function aging(diasVencido) {
  if (diasVencido <= 0) return { label: diasVencido === 0 ? 'Vence hoy' : `Faltan ${-diasVencido} días`, clase: 'bg-slate-100 text-slate-500' };
  if (diasVencido <= 30) return { label: `${diasVencido} días`, clase: 'bg-slate-100 text-slate-500' };
  if (diasVencido <= 60) return { label: `${diasVencido} días`, clase: 'bg-amber-50 text-amber-700' };
  return { label: `${diasVencido} días`, clase: 'bg-red-50 text-red-700' };
}

function TarjetaFactura({ f, verEn }) {
  const ag = aging(Number(f.dias_vencido));
  return (
    <div className="border border-slate-200 rounded-lg p-2.5 bg-white">
      <div className="flex items-center justify-between gap-1">
        <p className="text-sm font-medium text-slate-800 truncate">#{f.numero}</p>
        {f.indexacion && <span className="text-[10px] px-1.5 py-0.5 rounded bg-sky-50 text-sky-700">Indexada</span>}
      </div>
      <div className="flex items-center justify-between mt-1">
        <span className="text-sm font-semibold text-slate-800">{pref(verEn)}{formatMonto(montoVista(f, verEn))}</span>
        <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${ag.clase}`}>{ag.label}</span>
      </div>
      <p className="text-xs text-slate-400 mt-1">Vence {formatFecha(f.fecha_vencimiento)}</p>
    </div>
  );
}

export default function ReporteCuentasPorCobrar() {
  const { empresaActiva, empresaActivaNombre } = useAuth();
  const [facturas, setFacturas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [verEn, setVerEn] = useVistaMoneda();

  useEffect(() => {
    if (!empresaActiva) return;
    setCargando(true);
    setError('');
    api.get('/facturas/cxc')
      .then(({ data }) => setFacturas(data))
      .catch((err) => setError(err.response?.data?.error || 'No se pudieron cargar las cuentas por cobrar'))
      .finally(() => setCargando(false));
  }, [empresaActiva]);

  const { porCliente, total } = useMemo(() => {
    const grupos = new Map();
    let tot = 0;
    facturas.forEach((f) => {
      const m = montoVista(f, verEn);
      if (!grupos.has(f.cliente_id)) grupos.set(f.cliente_id, { nombre: f.cliente_nombre, facturas: [], subtotal: 0 });
      const g = grupos.get(f.cliente_id);
      g.facturas.push(f);
      g.subtotal += m;
      tot += m;
    });
    return { porCliente: [...grupos.values()], total: tot };
  }, [facturas, verEn]);

  function contenidoExcel() {
    return {
      titulo: 'Cuentas por cobrar',
      detalle: `Facturas por cobrar · montos en ${verEn}`,
      hojas: [{
        nombre: 'Por cobrar',
        columnas: [
          { titulo: 'Cliente' },
          { titulo: 'N° factura' },
          { titulo: 'Fecha', tipo: 'fecha' },
          { titulo: 'Vence', tipo: 'fecha' },
          { titulo: 'Días vencido', tipo: 'entero' },
          { titulo: 'Moneda' },
          { titulo: 'Saldo original', tipo: 'monto' },
          { titulo: `Saldo ${verEn}`, tipo: 'monto' }
        ],
        filas: facturas.map((f) => [
          f.cliente_nombre,
          f.numero,
          f.fecha ? String(f.fecha).slice(0, 10) : '',
          f.fecha_vencimiento ? String(f.fecha_vencimiento).slice(0, 10) : '',
          Number(f.dias_vencido) || 0,
          f.indexacion ? 'BS indexada' : f.moneda,
          f.indexacion ? f.indexacion.saldo_hoy_bs : Number(f.saldo) || 0,
          montoVista(f, verEn)
        ]),
        total: ['Total', '', '', '', null, '', null, total]
      }]
    };
  }

  return (
    <div className="space-y-6">
      <EncabezadoImpresion titulo="Cuentas por cobrar" detalle={`Montos en ${verEn}`} />
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between print:hidden">
        <h1 className="text-2xl font-bold text-slate-800">
          Cuentas por Cobrar
          {empresaActivaNombre && <span className="text-slate-400 font-normal"> · {empresaActivaNombre}</span>}
          <BotonAyuda />
        </h1>
        <div className="flex flex-wrap items-center gap-2">
          <SelectorMoneda vista={verEn} onChange={setVerEn} />
          <Exportar excel={contenidoExcel} disabled={facturas.length === 0} />
        </div>
      </div>

      {error && <p className="text-red-600 text-sm">{error}</p>}

      {cargando ? (
        <p className="text-slate-400 text-sm">Cargando…</p>
      ) : facturas.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center text-slate-400">
          No hay facturas de venta pendientes de cobro.
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-slate-700">Por cobrar</h2>
            <span className="text-sm text-slate-500">{pref(verEn)}{formatMonto(total)}</span>
          </div>
          {porCliente.map((g) => (
            <div key={g.nombre}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{g.nombre}</p>
                <span className="text-xs text-slate-400">{pref(verEn)}{formatMonto(g.subtotal)}</span>
              </div>
              <div className="grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-2">
                {g.facturas.map((f) => <TarjetaFactura key={f.id} f={f} verEn={verEn} />)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
